/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { CardSelect } from 'app/components/atoms/forms/fields/card-select/card-select';
import { Input } from 'app/components/ui/input';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { formatCurrency } from 'app/core/utils/formatter';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import type { Category } from 'app/types/category';
import type { SubCategory } from 'app/types/sub-category';
import type { Transaction } from 'app/types/transaction';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  items: Transaction[];
  onClose: () => void;
  onDone: () => void;
};

/**
 * Read a list payload defensively (array or paginated `{ results }`).
 */

function readList<T>(response: any): T[] {
  return response?.data?.data?.results ?? response?.data?.data ?? [];
}

/**
 * Export `BulkEditDrawer` component.
 *
 * Bulk-edits the category/sub-category of the selected movements at once, and
 * lets each selected movement's description be edited inline before saving.
 */

export function BulkEditDrawer({ items, onClose, onDone }: Props): JSX.Element {
  const [translate] = useTranslation();
  const client = useRequestProvider();
  const { success, error } = useToast();

  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [subCategoryId, setSubCategoryId] = useState<number | undefined>(undefined);
  const [descriptions, setDescriptions] = useState<Record<string, string>>(() =>
    Object.fromEntries(items.map(item => [String(item.id), item.description ?? '']))
  );
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchLists() {
      try {
        const [categoriesResponse, subCategoriesResponse] = await Promise.all([
          client.get(resolveAppUrl(apiEndpoints.categories)),
          client.get(resolveAppUrl(apiEndpoints.subCategories))
        ]);

        setCategories(readList<Category>(categoriesResponse));
        setSubCategories(readList<SubCategory>(subCategoriesResponse));
      } catch (fetchError) {
        console.error(fetchError);
      }
    }

    fetchLists();
  }, [client]);

  const categoryOptions = useMemo(
    () => categories.map(category => ({ label: category.name, value: category.id })),
    [categories]
  );

  const subCategoryOptions = useMemo(
    () =>
      categoryId
        ? subCategories
            .filter(subCategory => subCategory.categoryId === categoryId)
            .map(subCategory => ({ label: subCategory.name, value: subCategory.id }))
        : [],
    [subCategories, categoryId]
  );

  const handleChangeCategory = useCallback((value: any) => {
    setCategoryId(value ? Number(value) : undefined);
    setSubCategoryId(undefined);
  }, []);

  const handleApply = useCallback(async () => {
    if (!subCategoryId) {
      return;
    }

    setSaving(true);

    try {
      const changed = items
        .filter(item => (descriptions[String(item.id)] ?? '') !== (item.description ?? ''))
        .map(item => ({ id: item.id, description: descriptions[String(item.id)] ?? '' }));

      await client.post(resolveAppUrl(apiEndpoints.transactionsBulkCategory), {
        descriptions: changed,
        ids: items.map(item => item.id),
        subCategoryId
      });

      success({ message: translate('bankAccounts.movements.bulk.done', { count: items.length }) });
      onDone();
    } catch (saveError: any) {
      error({ message: saveError?.response?.data?.data?.message ?? translate('common.errors.network.default.title') });
    } finally {
      setSaving(false);
    }
  }, [client, descriptions, error, items, onDone, subCategoryId, success, translate]);

  return (
    <div className={'flex h-full flex-col'}>
      <div className={'mb-4'}>
        <h2 className={'text-lg font-semibold text-foreground'}>{translate('bankAccounts.movements.bulk.title')}</h2>

        <p className={'text-sm text-muted-foreground'}>
          {translate('bankAccounts.movements.bulk.subtitle', { count: items.length })}
        </p>
      </div>

      <div className={'space-y-3'}>
        <CardSelect
          label={translate('common.labels.category')}
          name={'category'}
          onChange={handleChangeCategory}
          options={categoryOptions}
          value={categoryId}
        />

        <CardSelect
          disabled={!categoryId}
          label={translate('common.labels.subCategory')}
          name={'subCategory'}
          onChange={(value: any) => setSubCategoryId(value ? Number(value) : undefined)}
          options={subCategoryOptions}
          value={subCategoryId}
        />
      </div>

      <div className={'mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground'}>
        {translate('bankAccounts.movements.bulk.descriptions')}
      </div>

      <div className={'mt-2 flex-1 space-y-2 overflow-y-auto'}>
        {items.map(item => (
          <div className={'flex items-center gap-2 rounded-md border border-border p-2'} key={item.id}>
            <span className={'w-16 shrink-0 text-xs text-muted-foreground'}>{formatCurrency(item.amount)}</span>

            <Input
              onChange={event => setDescriptions(current => ({ ...current, [String(item.id)]: event.target.value }))}
              value={descriptions[String(item.id)] ?? ''}
            />
          </div>
        ))}
      </div>

      <div className={'mt-4 flex justify-end gap-2 border-t border-border pt-4'}>
        <Button onClick={onClose} type={'default'}>
          {translate('common.actions.cancel')}
        </Button>

        <Button disabled={!subCategoryId} loading={isSaving} onClick={handleApply} type={'primary'}>
          {translate('common.actions.apply')}
        </Button>
      </div>
    </div>
  );
}
